/**
 * playSceneRestore.ts — Async save-restore helper extracted from PlayScene.
 *
 * Owns the read-modify-apply flow for hydrating Nova's ability flags and
 * restoring a saved checkpoint at scene init. Extracted as a free function
 * so PlayScene.ts stays under the 300-line cap (AGENTS.md).
 *
 * @module playSceneRestore
 */

import { gameState } from '../engine/gameState';
import { countCollectedHeartFragments, maxHealthForFragments } from '../data/heartUpgrades';
import { respawnNova } from '../engine/respawn';
import type { CheckpointSystem } from '../levels/CheckpointSystem';
import { load } from '../services/saveManager';
import Logger from '../utils/logger';
import type { SaveData } from '../types/save';

/** Context the helper needs to coordinate with the owning scene. */
export interface RestoreContext {
  /** Snapshot of `_initToken` at the call site — drops stale callbacks. */
  initToken: number;
  /** Level key for the per-level checkpoint lookup. */
  levelId: string;
  /** The active checkpoint system to mutate on restore. */
  checkpoints: CheckpointSystem;
  /** True while the scene has not been destroyed. */
  isActive: () => boolean;
  /** True while no later init/destroy cycle has advanced `_initToken`. */
  isCurrentToken: () => boolean;
  /** Capture the post-restore state as a new respawn snapshot. */
  takeSnapshot: () => void;
}

/**
 * Rebuild Nova's Heart Fragment upgrade from the save.
 *
 * `initGameState` resets `maxHealth` to `NOVA_MAX_HP` on every level load, so
 * the upgrade has to be re-derived here from the persisted fragment ids.
 * Fragments already banked in this level are also marked collected so they
 * cannot be picked up a second time and double-count.
 *
 * @param saveData - Loaded save, or null when there is none.
 * @param levelId  - Level key whose fragments should be suppressed.
 */
function applyHeartFragmentProgress(saveData: SaveData | null, levelId: string): void {
  const total = countCollectedHeartFragments(saveData);
  gameState.heartFragments = total;
  gameState.nova.maxHealth = maxHealthForFragments(total);
  // DECISION: Start the level at full health rather than carrying the old
  // 3 HP into a larger bar — a level always began full before Heart Fragments
  // persisted, and starting a 4-heart Nova on 3 hearts would look like damage
  // she never took.
  gameState.nova.health = gameState.nova.maxHealth;

  const bankedIds = saveData?.levels[levelId]?.collectedHeartFragmentIds;
  if (!bankedIds || bankedIds.length === 0) return;
  const banked = new Set(bankedIds);
  for (const c of gameState.collectibles) {
    if (c.kind === 'heart_fragment' && banked.has(c.id)) c.collected = true;
  }
}

/**
 * Load save data and apply checkpoint + ability hydration.
 * Pauses `gameState.isRunning` during the async window and re-enables it in
 * `finally` so a failed/stale load never leaves the loop frozen.
 *
 * @param ctx - Scene-supplied callbacks and identifiers.
 */
export function restoreCheckpointFromSave(ctx: RestoreContext): void {
  // DECISION: Pause game loop during async load so the player cannot
  // move/collect before restore resolves. < 5ms on device, imperceptible.
  gameState.isRunning = false;
  load()
    .then((saveData) => {
      if (!ctx.isActive() || !ctx.isCurrentToken()) return;
      // DECISION: Apply ability flags from save independently of checkpoint
      // restore. OR-merged with the dev-toggle seed — true wins.
      if (saveData?.abilities.canDoubleJump === true) gameState.nova.canDoubleJump = true;
      if (saveData?.abilities.canWallSlide === true) gameState.nova.canWallSlide = true;
      // DECISION: no dev-toggle counterpart for dash — `devUnlockWorld5` does
      // not exist, and adding one would drag in the world-unlock and settings
      // surfaces for a flag `persistDashUnlock()` already knows how to grant.
      // The save path alone makes the ability live the moment the World 4
      // boss-clear call site lands (tracked in ROADMAP Phase 4).
      if (saveData?.abilities.canDash === true) gameState.nova.canDash = true;
      // DECISION: Runs before the checkpoint branch so the max-HP upgrade
      // applies on every load, not only when a checkpoint exists.
      applyHeartFragmentProgress(saveData ?? null, ctx.levelId);
      if (ctx.checkpoints.activeCheckpointId) return;
      const levelProgress = saveData?.levels[ctx.levelId];
      if (!levelProgress?.lastCheckpointId) return;
      ctx.checkpoints.restoreFromSave(levelProgress.lastCheckpointId);
      if (!ctx.checkpoints.activeCheckpointId) return;
      const { x, y } = ctx.checkpoints.getRespawnPosition(gameState.spawnX, gameState.spawnY);
      gameState.checkpointSpawnX = x;
      gameState.checkpointSpawnY = y;
      respawnNova();
      // DECISION: Blanket-reset collectibles — Phase 2 checkpoint saves only
      // persist lastCheckpointId, not per-collectible IDs (Phase 4+ task).
      // Heart Fragments are excluded: they are permanent progression banked in
      // the save, so resetting them here would hand the player a second
      // pickup for a fragment they already own.
      for (const c of gameState.collectibles) {
        if (c.kind !== 'heart_fragment') c.collected = false;
      }
      gameState.seeds = 0;
      gameState.shardsCollected = 0;
      gameState.score = 0;
      ctx.takeSnapshot();
      Logger.info('PlayScene', `Restored checkpoint: ${levelProgress.lastCheckpointId}`);
    })
    .catch((err) => {
      Logger.warn('PlayScene', 'Failed to restore checkpoint from save', err);
    })
    .finally(() => {
      // DECISION: Re-enable regardless of success/failure/stale — the player
      // should always be able to play from wherever they end up.
      if (ctx.isCurrentToken() && ctx.isActive()) {
        gameState.isRunning = true;
      }
    });
}
